import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { CSRF_HEADER } from "./client";
import { keys } from "./queries";

export type LiveState = "connecting" | "live" | "offline";

/**
 * Délai avant la n-ième tentative de reconnexion : rapide d'abord, puis toutes les 30 s, sans jamais abandonner (PB-103).
 * Le défaut de SignalR s'arrête après 4 essais : le board restait figé jusqu'au rechargement de la page.
 */
export function retryDelay(attempt: number): number {
  return [0, 2_000, 5_000, 10_000][attempt] ?? 30_000;
}

/** Hors ligne, les données sont rechargées à ce rythme (le temps réel ne les pousse plus). */
export const OFFLINE_REFRESH_MS = 30_000;

/** Rafales : on regroupe sur 250 ms, mais on rafraîchit au plus tard après 1 s même si les événements continuent. */
const DEBOUNCE_MS = 250;
const MAX_WAIT_MS = 1_000;

/**
 * Temps réel : SignalR n'apporte qu'un « poke » (seq, type) ; la vérité reste l'API.
 * Chaque poke invalide les requêtes du projet, regroupées pour absorber les rafales.
 */
export function useProjectEvents(tenant: string, project: string): LiveState {
  const client = useQueryClient();
  const [state, setState] = useState<LiveState>("connecting");

  useEffect(() => {
    setState("connecting");
    let disposed = false;
    let teardown: (() => void) | undefined;
    // SignalR est chargé à la demande (PB-108) : la page de connexion et l'accueil ne l'embarquent pas.
    void import("@microsoft/signalr").then(
      (signalr) => {
        if (!disposed) teardown = connect(signalr);
      },
      () => !disposed && setState("offline"),
    );

    function connect({ HubConnectionBuilder, HubConnectionState, LogLevel }: typeof import("@microsoft/signalr")) {
      const connection = new HubConnectionBuilder()
        // Cookie de session envoyé (même origine) ; la négociation est un POST : en-tête anti-CSRF comme le reste de l'API.
        .withUrl("/hubs/events", { headers: { [CSRF_HEADER]: "1" } })
        .withAutomaticReconnect({ nextRetryDelayInMilliseconds: (context) => retryDelay(context.previousRetryCount) })
        .configureLogging(LogLevel.Warning)
        .build();

      let debounce: ReturnType<typeof setTimeout> | undefined;
      let firstPending: number | undefined;
      const invalidate = () => {
        firstPending = undefined;
        void client.invalidateQueries({ queryKey: keys.project(tenant, project) });
        void client.invalidateQueries({ queryKey: keys.me }); // compteurs des onglets
      };
      const refresh = () => {
        clearTimeout(debounce);
        firstPending ??= Date.now();
        const waited = Date.now() - firstPending;
        debounce = setTimeout(invalidate, waited >= MAX_WAIT_MS ? 0 : Math.min(DEBOUNCE_MS, MAX_WAIT_MS - waited));
      };

      // Hors ligne : rechargement régulier, et nouvelle tentative de connexion (le serveur peut revenir).
      let offlineTimer: ReturnType<typeof setInterval> | undefined;
      let restartTimer: ReturnType<typeof setTimeout> | undefined;
      const goOffline = () => {
        if (disposed) return;
        setState("offline");
        offlineTimer ??= setInterval(invalidate, OFFLINE_REFRESH_MS);
        clearTimeout(restartTimer);
        restartTimer = setTimeout(start, OFFLINE_REFRESH_MS);
      };
      const goLive = () => {
        if (disposed) return;
        clearInterval(offlineTimer);
        offlineTimer = undefined;
        setState("live");
      };
      const join = () =>
        connection.invoke("Join", tenant, project).then(goLive, () => {
          // Join refusé (projet retiré, session expirée) : on coupe ; la reconnexion réessaiera.
          if (!disposed) void connection.stop();
        });
      function start() {
        if (disposed || connection.state !== HubConnectionState.Disconnected) return;
        setState("connecting");
        connection.start().then(join, goOffline);
      }

      connection.on("event", refresh);
      connection.onreconnecting(() => !disposed && setState("connecting"));
      connection.onreconnected(() => {
        void join();
        refresh(); // rattrapage de ce qui a été manqué pendant la coupure
      });
      connection.onclose(goOffline);
      start();

      return () => {
        clearTimeout(debounce);
        clearTimeout(restartTimer);
        clearInterval(offlineTimer);
        if (connection.state !== HubConnectionState.Disconnected) void connection.stop();
      };
    }

    return () => {
      disposed = true;
      teardown?.();
    };
  }, [client, tenant, project]);

  return state;
}
